import React from 'react'
import { configService, NetworkType } from '@/config'

/**
 * Static-looking network label with a hidden network toggle.
 *
 * Replaces the removed network-switcher dropdown. Renders the current network
 * name as plain label text (e.g. "Mainnet"). The FINAL letter "t" — which both
 * "Mainnet" and "Testnet" happen to share — is a deliberately obscure,
 * visually-indistinguishable switch: clicking it flips mainnet↔testnet via
 * configService.setCurrentNetwork(), which persists the choice (localStorage)
 * and fires `networkChanged` → NetworkSwitchManager invalidates the query cache
 * so data reloads for the new network. No underline / color / pointer cursor /
 * tooltip: it looks exactly like the rest of the word.
 */
export const NetworkLabel: React.FC = () => {
  const [network, setNetwork] = React.useState<NetworkType>(() => configService.getCurrentNetwork())

  React.useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.currentNetwork) setNetwork(detail.currentNetwork as NetworkType)
    }
    window.addEventListener('networkChanged', onChange as EventListener)
    return () => window.removeEventListener('networkChanged', onChange as EventListener)
  }, [])

  const label = network.charAt(0).toUpperCase() + network.slice(1) // "Mainnet" / "Testnet"
  const head = label.slice(0, -1)  // "Mainne" / "Testne"
  const tail = label.slice(-1)     // "t"
  const other: NetworkType = network === 'mainnet' ? 'testnet' : 'mainnet'

  const flip = (e: React.MouseEvent) => {
    e.stopPropagation()
    configService.setCurrentNetwork(other)
  }

  return (
    <span className="hidden sm:inline text-xs font-medium text-gray-500 dark:text-gray-400 select-none" aria-label={`${label} network`}>
      {head}
      {/* Hidden network toggle — identical styling to the rest of the word. */}
      <span onClick={flip} className="cursor-default">{tail}</span>
    </span>
  )
}

export default NetworkLabel
